import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaUniversity, FaUserPlus, FaNewspaper, FaUsers } from 'react-icons/fa';
import Navbar from '../student/Navbar';
import Loader from '../components/Loader';
import SuccessModal from '../components/SuccessModal';
import { useAuth } from '../context/AuthContext';
import SuperAdminSidebar from './SuperAdminSidebar';
import RoleSwitcher from './RoleSwitcher';

const SuperAdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUniversityModal, setShowUniversityModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successTitle, setSuccessTitle] = useState('Success');
  const [universities, setUniversities] = useState([]);
  const [analytics, setAnalytics] = useState({ totalUniversities: 0, totalAccounts: 0, recentActivity: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const location = useLocation();
  const { editUniversity } = location.state || {};

  // University form state
  const [universityForm, setUniversityForm] = useState({
    universityId: '',
    name: { en: '', ar: '' },
    address: { en: '', ar: '' },
    contactInfo: { phone: '', email: '', website: '' },
    description: { en: '', ar: '' },
    establishedYear: '',
    accreditation: { en: '', ar: '' },
    faculties: [],
    programs: [],
    studentCapacity: '',
    tuitionFees: { local: '', international: '' },
    admissionRequirements: { en: '', ar: '' },
    campusSize: '',
    numberOfFaculty: '',
    researchCenters: [],
    partnerships: [],
    rankings: { national: '', international: '' },
    facilities: [],
    scholarships: [],
    languages: [],
    semesterSystem: 'semester',
    academicCalendar: {
      fallStart: '',
      fallEnd: '',
      springStart: '',
      springEnd: '',
      summerStart: '',
      summerEnd: ''
    },
    holidays: ['Friday', 'Sunday']
  });

  // Account form state
  const [accountForm, setAccountForm] = useState({
    email: '',
    password: '',
    name: '',
    role: '',
    studentId: '',
    universityId: '',
    clinicId: '',
    affiliationType: 'university', // 'university' or 'dentist' for assistants
    dentistId: '',
    accountType: 'regular', // 'regular' or 'google' or 'microsoft'
  });

  // News form state
  const [newsForm, setNewsForm] = useState({
    title: { en: '', ar: '' },
    content: { en: '', ar: '' },
    category: '',
    priority: 'medium',
    targetAudience: 'all',
    publishDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    isActive: true
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !token) {
        navigate('/login');
        return;
      }

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const [universitiesRes, analyticsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/universities`), // No auth required
          axios.get(`${process.env.REACT_APP_API_URL}/api/superadmin/superadmin`, config),
        ]);

        setUniversities(universitiesRes.data || []);
        setAnalytics(analyticsRes.data || { totalUniversities: 0, totalAccounts: 0, recentActivity: [] });
      } catch (err) {
        const errorMessage = err.response?.status === 401
          ? 'Unauthorized. Please log in again.'
          : err.response?.data?.message || 'Failed to load data';
        setError(errorMessage);
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    if (editUniversity) setShowUniversityModal(true);
  }, [user, token, navigate, editUniversity]);

  const handleUniversitySubmit = async (e) => {
    e.preventDefault();
    try {
      if (!universityForm.universityId.trim()) {
        setError('University ID is required');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editUniversity) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/universities/${universityForm.universityId}`, universityForm, config);
        setUniversities(universities.map(u => u.universityId === universityForm.universityId ? { ...u, ...universityForm } : u));
        setSuccessTitle('University Updated');
        setSuccessMessage('University has been updated successfully!');
      } else {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/universities`, universityForm, config);
        setUniversities([...universities, response.data]);
        setSuccessTitle('University Added');
        setSuccessMessage('University has been added successfully!');
      }
      
      setShowSuccessModal(true);
      setShowUniversityModal(false);
      
      // Reset form
      setUniversityForm({
        universityId: '',
        name: { en: '', ar: '' },
        address: { en: '', ar: '' },
        contactInfo: { phone: '', email: '', website: '' },
        description: { en: '', ar: '' },
        establishedYear: '',
        accreditation: { en: '', ar: '' },
        faculties: [],
        programs: [],
        studentCapacity: '',
        tuitionFees: { local: '', international: '' },
        admissionRequirements: { en: '', ar: '' },
        campusSize: '',
        numberOfFaculty: '',
        researchCenters: [],
        partnerships: [],
        rankings: { national: '', international: '' },
        facilities: [],
        scholarships: [],
        languages: [],
        semesterSystem: 'semester',
        academicCalendar: {
          fallStart: '',
          fallEnd: '',
          springStart: '',
          springEnd: '',
          summerStart: '',
          summerEnd: ''
        },
        holidays: ['Friday', 'Sunday']
      });

      // Refresh data
      const [newUniversitiesRes, analyticsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/universities`), // No auth required
        axios.get(`${process.env.REACT_APP_API_URL}/api/superadmin/superadmin`, config),
      ]);
      setUniversities(newUniversitiesRes.data || []);
      setAnalytics(analyticsRes.data || { totalUniversities: 0, totalAccounts: 0, recentActivity: [] });

      navigate('/superadmin/dashboard', { state: {} });
    } catch (err) {
      const errorMessage = err.response?.status === 401
        ? 'Unauthorized. Please log in again.'
        : err.response?.data?.message || 'Failed to save university';
      setError(errorMessage);
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Clean form data based on role and account type
      const cleanedData = { ...accountForm };
      
      // Remove studentId if not student
      if (accountForm.role !== 'student') {
        delete cleanedData.studentId;
      }
      
      // Remove universityId if not needed
      if (!(['student', 'supervisor', 'admin', 'it'].includes(accountForm.role) || (accountForm.role === 'assistant' && accountForm.affiliationType === 'university'))) {
        delete cleanedData.universityId;
      }
      
      // Remove dentistId if not assistant with dentist affiliation or if empty
      if (!(accountForm.role === 'assistant' && accountForm.affiliationType === 'dentist' && accountForm.dentistId)) {
        delete cleanedData.dentistId;
      }
      
      // Remove clinicId if not dentist
      if (accountForm.role !== 'dentist') {
        delete cleanedData.clinicId;
      }
      
      // Remove affiliationType if not assistant
      if (accountForm.role !== 'assistant') {
        delete cleanedData.affiliationType;
      }
      
      // For Google/Microsoft accounts, remove password and add flags
      if (accountForm.accountType === 'google') {
        delete cleanedData.password;
        cleanedData.isGoogleAccount = true;
      } else if (accountForm.accountType === 'microsoft') {
        delete cleanedData.password;
        cleanedData.isMicrosoftAccount = true;
      }
      
      // Remove accountType from the data sent to backend
      delete cleanedData.accountType;
      
      // Use different endpoint for Google/Microsoft accounts
      const endpoint = accountForm.accountType === 'google'
        ? `${process.env.REACT_APP_API_URL}/api/accounts/google`
        : accountForm.accountType === 'microsoft'
          ? `${process.env.REACT_APP_API_URL}/api/accounts/microsoft`
          : `${process.env.REACT_APP_API_URL}/api/accounts`;
      
      await axios.post(endpoint, cleanedData, config);
      
      setShowAccountModal(false);
      setAccountForm({
        email: '',
        password: '',
        name: '',
        role: '',
        studentId: '',
        universityId: '',
        clinicId: '',
        affiliationType: 'university',
        dentistId: '',
        accountType: 'regular',
      });
      
      setSuccessTitle('Account Created');
      setSuccessMessage('Account has been created successfully!');
      setShowSuccessModal(true);

      // Refresh analytics
      const analyticsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/superadmin/superadmin`, config);
      setAnalytics(analyticsRes.data || { totalUniversities: 0, totalAccounts: 0, recentActivity: [] });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add account');
    }
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${process.env.REACT_APP_API_URL}/api/news`, newsForm, config);
      
      setShowNewsModal(false);
      setNewsForm({
        title: { en: '', ar: '' },
        content: { en: '', ar: '' },
        category: '',
        priority: 'medium',
        targetAudience: 'all',
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: '',
        isActive: true
      });
      
      setSuccessTitle('News Published');
      setSuccessMessage('News has been published successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to publish news');
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <SuperAdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Superadmin Dashboard</h1>
              <p className="text-gray-600">Manage universities, accounts, and system-wide settings</p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Open any role's dashboard in its own tab, as that role's test
                account, without signing out of this session. */}
            <RoleSwitcher token={token} />

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => setShowUniversityModal(true)}
              >
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FaUniversity className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add University</h3>
                    <p className="text-gray-600">Create new university</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => setShowAccountModal(true)}
              >
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FaUserPlus className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add Account</h3>
                    <p className="text-gray-600">Create user account</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => setShowNewsModal(true)}
              >
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <FaNewspaper className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Publish News</h3>
                    <p className="text-gray-600">Create news article</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => navigate('/superadmin/accounts')}
              >
                <div className="flex items-center">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <FaUsers className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Accounts</h3>
                    <p className="text-gray-600">View all accounts</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Analytics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Universities</p>
                    <p className="text-3xl font-bold text-blue-600">{analytics.totalUniversities || 0}</p>
                  </div>
                  <FaUniversity className="h-8 w-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Accounts</p>
                    <p className="text-3xl font-bold text-green-600">{analytics.totalAccounts || 0}</p>
                  </div>
                  <FaUsers className="h-8 w-8 text-green-600" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Recent Activity</p>
                    <p className="text-3xl font-bold text-purple-600">{analytics.recentActivity?.length || 0}</p>
                  </div>
                  <FaNewspaper className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </div>

            {/* Recent Universities */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Universities</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">University ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Contact</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {universities.slice(0, 5).map((university) => (
                      <tr key={university.universityId} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">{university.universityId}</td>
                        <td className="py-3 px-4">{university.name?.en || 'N/A'}</td>
                        <td className="py-3 px-4">{university.contactInfo?.email || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => navigate('/superadmin/universities')}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>

        {/* University Modal */}
        {showUniversityModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">
                {editUniversity ? 'Edit University' : 'Add New University'}
              </h2>
              <form onSubmit={handleUniversitySubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">University ID*</label>
                    <input
                      type="text"
                      value={universityForm.universityId}
                      onChange={(e) => setUniversityForm({...universityForm, universityId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name (English)*</label>
                    <input
                      type="text"
                      value={universityForm.name.en}
                      onChange={(e) => setUniversityForm({...universityForm, name: {...universityForm.name, en: e.target.value}})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name (Arabic)</label>
                    <input
                      type="text"
                      value={universityForm.name.ar}
                      onChange={(e) => setUniversityForm({...universityForm, name: {...universityForm.name, ar: e.target.value}})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={universityForm.contactInfo.email}
                      onChange={(e) => setUniversityForm({...universityForm, contactInfo: {...universityForm.contactInfo, email: e.target.value}})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="text"
                      value={universityForm.contactInfo.phone}
                      onChange={(e) => setUniversityForm({...universityForm, contactInfo: {...universityForm.contactInfo, phone: e.target.value}})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <input
                      type="url"
                      value={universityForm.contactInfo.website}
                      onChange={(e) => setUniversityForm({...universityForm, contactInfo: {...universityForm.contactInfo, website: e.target.value}})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUniversityModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editUniversity ? 'Update' : 'Create'} University
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Account Modal */}
        {showAccountModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Add New Account</h2>
              <form onSubmit={handleAccountSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Type*</label>
                    <select
                      value={accountForm.accountType}
                      onChange={(e) => setAccountForm({...accountForm, accountType: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    >
                      <option value="regular">Regular Account</option>
                      <option value="google">Google Account</option>
                      <option value="microsoft">Microsoft Account</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                    <input
                      type="email"
                      value={accountForm.email}
                      onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  {accountForm.accountType === 'regular' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Password*</label>
                      <div className="relative">
                        <input
                          type="password"
                          value={accountForm.password}
                          onChange={(e) => setAccountForm({...accountForm, password: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                    <input
                      type="text"
                      value={accountForm.name}
                      onChange={(e) => setAccountForm({...accountForm, name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role*</label>
                    <select
                      value={accountForm.role}
                      onChange={(e) => setAccountForm({...accountForm, role: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    >
                      <option value="">Select Role</option>
                      <option value="student">Student</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="admin">Admin</option>
                      <option value="assistant">Assistant</option>
                      <option value="dentist">Dentist</option>
                      <option value="it">IT</option>
                    </select>
                  </div>
                </div>

                {accountForm.role === 'student' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Student ID*</label>
                    <input
                      type="text"
                      value={accountForm.studentId}
                      onChange={(e) => setAccountForm({...accountForm, studentId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}

                {accountForm.role === 'assistant' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Affiliation Type*</label>
                    <select
                      value={accountForm.affiliationType}
                      onChange={(e) => setAccountForm({...accountForm, affiliationType: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="university">University</option>
                      <option value="dentist">Dentist</option>
                    </select>
                  </div>
                )}

                {accountForm.role === 'assistant' && accountForm.affiliationType === 'university' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">University*</label>
                    <select
                      value={accountForm.universityId}
                      onChange={(e) => setAccountForm({...accountForm, universityId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select University</option>
                      {universities.map((university) => (
                        <option key={university.universityId} value={university.universityId}>
                          {university.name?.en || university.universityId}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {accountForm.role === 'assistant' && accountForm.affiliationType === 'dentist' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dentist ID</label>
                    <input
                      type="text"
                      value={accountForm.dentistId}
                      onChange={(e) => setAccountForm({...accountForm, dentistId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter dentist ID (optional)"
                    />
                  </div>
                )}

                {['student', 'supervisor', 'admin', 'it'].includes(accountForm.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">University*</label>
                    <select
                      value={accountForm.universityId}
                      onChange={(e) => setAccountForm({...accountForm, universityId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select University</option>
                      {universities.map((university) => (
                        <option key={university.universityId} value={university.universityId}>
                          {university.name?.en || university.universityId}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {accountForm.role === 'dentist' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clinic ID*</label>
                    <input
                      type="text"
                      value={accountForm.clinicId}
                      onChange={(e) => setAccountForm({...accountForm, clinicId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAccountModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Create Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* News Modal */}
        {showNewsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
              <h2 className="text-xl font-bold mb-4">Publish News</h2>
              <form onSubmit={handleNewsSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title (English)*</label>
                  <input
                    type="text"
                    value={newsForm.title.en}
                    onChange={(e) => setNewsForm({...newsForm, title: {...newsForm.title, en: e.target.value}})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content (English)*</label>
                  <textarea
                    value={newsForm.content.en}
                    onChange={(e) => setNewsForm({...newsForm, content: {...newsForm.content, en: e.target.value}})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="4"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={newsForm.category}
                      onChange={(e) => setNewsForm({...newsForm, category: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Category</option>
                      <option value="announcement">Announcement</option>
                      <option value="update">System Update</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="feature">New Feature</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={newsForm.priority}
                      onChange={(e) => setNewsForm({...newsForm, priority: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowNewsModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Publish News
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <SuccessModal
            title={successTitle}
            message={successMessage}
            onClose={() => setShowSuccessModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
